import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn } from "../components/UI";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2, CheckCircle2, Palette, Send } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-cyan-400/10 border-cyan-400/30 text-cyan-300",
  promoted: "bg-emerald-400/10 border-emerald-400/30 text-emerald-300",
};

export default function AdStudioRefreshes() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/political-os/ad-studio/refreshes");
      setDrafts(data.drafts || []);
    } catch (e) { toast.error(errMsg(e, "Could not load drafts")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async (force = false) => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/political-os/ad-studio/refresh${force ? "?force=true" : ""}`);
      toast.success(`Generated ${data.generated} new draft${data.generated === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} skipped — already refreshed this week)` : ""}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Refresh failed")); }
    finally { setRefreshing(false); }
  };

  const promote = async (id) => {
    try {
      await api.post(`/political-os/ad-studio/drafts/${id}/promote`);
      toast.success("Draft promoted — ready to push to Ad Studio");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const push = async (id, idx) => {
    try {
      const { data } = await api.post(`/political-os/ad-studio/drafts/${id}/push?variant_index=${idx}`);
      toast.success(`Variant ${idx + 1} pushed live · TotalReach ad id ${(data.stackadapt_ad_id || '').slice(0, 10)}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Push failed")); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="ad-studio-refreshes-page">
      <PageHeader eyebrow="Ad Studio · Weekly Refresh"
        title="Fresh Creative Ideas"
        subtitle="Every Monday, Ask TotalReach drafts three new creative variants for each of your active campaigns using your Brand Library. Review, tweak and promote."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => refresh(false)} disabled={refreshing} data-testid="refresh-run-btn">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run refresh
            </Btn>
            <Btn onClick={() => refresh(true)} disabled={refreshing} data-testid="refresh-force-btn">
              <Sparkles className="w-4 h-4" /> Force regenerate
            </Btn>
          </div>
        }
      />

      {loading && <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>}

      {!loading && drafts.length === 0 && (
        <Card>
          <div className="py-8 text-center">
            <Palette className="w-8 h-8 text-cyan-300/60 mx-auto mb-2" />
            <div className="text-sm text-slate-300 mb-1">No creative drafts yet</div>
            <div className="text-xs text-slate-500 mb-4">Click <b>Run refresh</b> above to draft variants right now, or wait for the Monday auto-refresh.</div>
            <a href="/brand-library" className="text-xs text-cyan-300 hover:underline">
              Tune your Brand Library first →
            </a>
          </div>
        </Card>
      )}

      {!loading && drafts.length > 0 && (
        <div className="space-y-4" data-testid="drafts-list">
          {drafts.map((d) => (
            <Card key={d.id} data-testid={`draft-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{d.week_key} · {(d.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  <div className="font-display text-lg font-bold text-white">{d.campaign_name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLES[d.status] || STATUS_STYLES.draft}`} data-testid={`draft-status-${d.id}`}>{d.status}</span>
                  {d.status !== "promoted" && (
                    <button onClick={() => promote(d.id)} data-testid={`draft-promote-${d.id}`}
                            className="text-xs px-3 py-1 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Promote
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(d.variants || []).map((v, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`draft-variant-${d.id}-${i}`}>
                    <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-medium mb-1">Variant {i + 1}</div>
                    <div className="text-sm text-white font-semibold leading-snug">{v.headline}</div>
                    <div className="text-xs text-slate-300 mt-1.5 leading-relaxed">{v.body}</div>
                    <div className="mt-2 pt-2 border-t border-white/5 text-[11px] text-emerald-300 font-medium">→ {v.cta}</div>
                    {d.status === "promoted" && (
                      <button
                        onClick={() => push(d.id, i)}
                        disabled={!!v.stackadapt_ad_id}
                        data-testid={`draft-push-${d.id}-${i}`}
                        className={`mt-3 w-full text-[11px] px-2 py-1.5 rounded-md border inline-flex items-center justify-center gap-1 transition ${v.stackadapt_ad_id
                          ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-300 cursor-default"
                          : "border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10"}`}
                      >
                        {v.stackadapt_ad_id ? (
                          <>
                            <CheckCircle2 className="w-3 h-3" /> Live · {v.stackadapt_ad_id.slice(0, 12)}…
                          </>
                        ) : (
                          <>
                            <Send className="w-3 h-3" /> Push live
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
